import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { AnalyticsService } from '@se-po/shared-data-access-services'
import { SeFeToastService } from 'se-fe-toast'
import { Persona } from '../../models'
import { ProfileMergeModalComponent } from '../profile-merge-modal/profile-merge-modal.component'

interface Dupe {
  name: string
  personas: Persona[]
  dismissed: boolean
  multiAccount: boolean
}

@Component({
  selector: 'app-duplicate-banner',
  templateUrl: './duplicate-banner.component.html',
  styleUrls: ['./duplicate-banner.component.scss']
})
export class DuplicateBannerComponent implements OnInit {
  @Input() public personas: Persona[]
  @Input() public persona: Persona
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() public onMerge = new EventEmitter()
  @ViewChild('profileMergeModal') public profileMergeModal: ProfileMergeModalComponent

  public duplicates: Dupe[]
  public keepPersona: Persona
  public mergePersona: Persona

  constructor(
    private analyticsService: AnalyticsService,
    private toast: SeFeToastService,
    private translateService: TranslateService
  ) { }

  public ngOnInit(): void {
    this.analyticsService.seEvent('ReviewDuplicates.MessageVisible', 8)
    this.duplicates = this.checkForDupes()
  }

  public checkForDupes(): any {
    const dupes: Dupe[] = []
    this.personas.forEach(persona => {
      const fullName = this.fullName(persona)
      if(this.isDuplicate(persona)){
        const dupe = dupes.find(_dupe => _dupe.name.toLowerCase() === fullName.toLowerCase())
        if (dupe) {
          dupe.personas.push(persona)
          dupe.multiAccount = dupe.personas.some(p => p.owner.id !== persona.owner.id )
        } else {
          dupes.push({
            name: fullName,
            personas: [persona],
            dismissed: false,
            multiAccount: false
          })
        }
      }
   })
   return dupes
  }

  public isDuplicate(persona): boolean {
    return this.personas.some(persona2 =>
      this.fullName(persona).toLowerCase() === this.fullName(persona2).toLowerCase() && persona.id !== persona2.id)
  }

  public openMergeModal(duplicate) {
    const selfDupe = duplicate.personas.find(p=> p.persona_type === 'self')
    this.analyticsService.seEvent('ReviewDuplicates.YesClick', 8)
    this.keepPersona = selfDupe ? selfDupe : duplicate.personas[0]
    this.mergePersona = selfDupe ? duplicate.personas.filter(p => p !== selfDupe )[0] : duplicate.personas[1]
    this.profileMergeModal.open()
  }

  public dismissBanner(duplicate) {
    this.analyticsService.seEvent('ReviewDuplicates.NoClick', 8)
    this.duplicates.find(dupe => dupe.name === duplicate.name).dismissed = true
  }

  public onSuccessfulMerge() {
    // Remove the merged persona on the client-side to avoid
    // timing issues encountered when re-fetching personas.
    // Merged persona may still be included in personas response
    // if refreshed immediately after successful merge
    this.personas = this.personas.filter(p => p.id !== this.mergePersona.id)
    this.checkForDupes()
    this.onMerge.emit()
    this.toast.success(this.translateService.instant(
      'PERSONAS.merge_success',
      {
        keep: this.fullName(this.keepPersona),
        merge: this.fullName(this.mergePersona)
      }
    ))
  }

  private fullName(persona: Persona): string {
    return persona.first_name + ' ' + persona.last_name
  }
}
